import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { X, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { fdiToHaderup } from '@/utils/toothNotation';
import type { Database } from '@/types/database.generated';

type TreatmentRow = Database['public']['Tables']['tooth_treatments']['Row'];

// ── Props ─────────────────────────────────────────────────────────────────────

interface TreatmentDetailDrawerProps {
  treatment: TreatmentRow | null;
  onClose: () => void;
}

// ── Helper ────────────────────────────────────────────────────────────────────

function LabeledRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="w-32 flex-shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500 pt-0.5">
        {label}
      </span>
      <span className="flex-1 text-sm text-slate-800 dark:text-slate-100 min-w-0">{value}</span>
    </div>
  );
}

const SURFACES = ['B', 'L', 'M', 'D', 'O'] as const;

// ── Component ─────────────────────────────────────────────────────────────────

export function TreatmentDetailDrawer({ treatment, onClose }: TreatmentDetailDrawerProps) {
  const { t } = useTranslation();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  const isOpen = treatment !== null;

  const anesthesia =
    treatment?.anesthesia && typeof treatment.anesthesia === 'object'
      ? (treatment.anesthesia as { type?: string; method?: string; amount?: string })
      : null;

  const medications = Array.isArray(treatment?.medications)
    ? (treatment!.medications as Array<{
        name: string;
        strength?: string;
        dosage?: string;
        frequency?: string;
        duration?: string;
      }>)
    : [];

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/30 transition-opacity duration-200',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('dental.history.title')}
        onKeyDown={handleKeyDown}
        className={cn(
          'fixed right-0 top-0 z-50 h-full w-full max-w-[480px]',
          'bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700',
          'flex flex-col overflow-hidden',
          'transform transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {treatment && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 flex-shrink-0">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Tand {treatment.tooth_fdi} ({fdiToHaderup(treatment.tooth_fdi)}) — {treatment.treatment_type}
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {format(new Date(treatment.treatment_date), 'd. MMMM yyyy', { locale: da })}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
              {/* Other practice banner */}
              {treatment.performed_at_other_practice && (
                <div className="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 mb-3">
                  <Info className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Udført på anden klinik
                    {treatment.other_practice_name && `: ${treatment.other_practice_name}`}
                  </span>
                </div>
              )}

              <LabeledRow label={t('dental.history.columns.description')} value={treatment.treatment_description} />
              <LabeledRow label="Diagnose" value={treatment.diagnosis} />
              <LabeledRow
                label={t('dental.history.columns.performer')}
                value={treatment.performed_by}
              />
              <LabeledRow label={t('dental.history.columns.date')} value={format(new Date(treatment.treatment_date), 'd. MMMM yyyy', { locale: da })} />

              {/* Surfaces */}
              {treatment.surfaces_treated && treatment.surfaces_treated.length > 0 && (
                <div className="flex gap-3 py-2 border-b border-slate-100 dark:border-slate-800">
                  <span className="w-32 flex-shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500 pt-0.5">
                    Flader
                  </span>
                  <div className="flex gap-1 flex-wrap">
                    {SURFACES.map((s) => (
                      <span
                        key={s}
                        className={cn(
                          'h-7 w-7 rounded-lg border text-xs font-semibold flex items-center justify-center',
                          treatment.surfaces_treated?.includes(s)
                            ? 'border-indigo-600 bg-indigo-600 text-white'
                            : 'border-slate-200 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-800',
                        )}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <LabeledRow label="Materiale" value={treatment.material_used} />

              {/* Anesthesia */}
              {anesthesia && (anesthesia.type || anesthesia.method || anesthesia.amount) && (
                <div className="flex gap-3 py-2 border-b border-slate-100 dark:border-slate-800">
                  <span className="w-32 flex-shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500 pt-0.5">
                    {t('dental.anesthesia.title')}
                  </span>
                  <div className="text-sm text-slate-800 dark:text-slate-100 space-y-0.5">
                    {anesthesia.type && <p>{t('dental.anesthesia.type')}: {anesthesia.type}</p>}
                    {anesthesia.method && <p>{t('dental.anesthesia.method')}: {anesthesia.method}</p>}
                    {anesthesia.amount && <p>{t('dental.anesthesia.amount')}: {anesthesia.amount}</p>}
                  </div>
                </div>
              )}

              {/* Medications */}
              {medications.length > 0 && (
                <div className="flex gap-3 py-2 border-b border-slate-100 dark:border-slate-800">
                  <span className="w-32 flex-shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500 pt-0.5">
                    {t('dental.medications.title')}
                  </span>
                  <div className="text-sm text-slate-800 dark:text-slate-100 space-y-1">
                    {medications.map((med, i) => (
                      <p key={i}>
                        {med.name}
                        {med.strength && ` ${med.strength}`}
                        {med.dosage && ` · ${med.dosage}`}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Consent */}
              <div className="flex gap-3 py-2 border-b border-slate-100 dark:border-slate-800">
                <span className="w-32 flex-shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500 pt-0.5">
                  {t('dental.consent.title')}
                </span>
                <div className="text-sm">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      treatment.consent_given
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                    )}
                  >
                    {treatment.consent_given ? t('dental.consent.given') : 'Ikke registreret'}
                  </span>
                  {treatment.consent_method && (
                    <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
                      ({treatment.consent_method})
                    </span>
                  )}
                </div>
              </div>

              <LabeledRow
                label={t('dental.costs.estimated')}
                value={
                  treatment.estimated_cost != null
                    ? `${treatment.estimated_cost.toLocaleString('da-DK')} kr`
                    : null
                }
              />
              <LabeledRow
                label={t('dental.costs.actual')}
                value={
                  treatment.actual_cost != null
                    ? `${treatment.actual_cost.toLocaleString('da-DK')} kr`
                    : null
                }
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-5 py-3.5 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
